import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// Relative imports, not the `~/...` alias: vitest.config.ts (unlike
// vite.config.ts) doesn't declare the `~` alias, so a real (non-type-only)
// `~/...` import fails to resolve under `vitest run` — every other existing
// test in this app sidesteps that by using relative imports or `import type`
// (which esbuild strips before resolution ever runs). `~` just points at
// `./src`, so these resolve to the exact same files under the real build.
import { authStore } from "../../stores/auth-store";
import { getApiUrl } from "../../lib/api";
import type {
  ProfileResponse,
  PurchasesData,
  ReviewsData,
  WaitlistData,
  MembershipsData,
} from "./types";

/**
 * Fetch helper shared by every /widget/me/* call in this hook. Never logs the
 * response or the request — a caller that wants to log a failure must go
 * through `describeErrorForLog` (apps/webcomponents/src/widgets/lib/utils.ts)
 * rather than logging this function's thrown Error or the raw fetch args,
 * since `init` carries the live bearer token in its Authorization header.
 */
export async function authedJson<T>(
  url: string,
  token: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { ...(init?.headers ?? {}), Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error("SESSION_EXPIRED");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

export function useProfileData(args: {
  apiUrl?: string;
  businessId: string;
  businessSlug: string;
  token: string | null;
}) {
  const root = (args.apiUrl || getApiUrl()).replace(/\/+$/, "");
  const token = args.token ?? "";
  const enabled = !!token && !!args.businessId;
  const qc = useQueryClient();

  const profile = useQuery({
    queryKey: ["me", "profile", token],
    enabled,
    queryFn: () => authedJson<ProfileResponse>(`${root}/widget/me/profile`, token),
    throwOnError: false,
  });
  const purchases = useQuery({
    queryKey: ["me", "purchases", token, args.businessId],
    enabled,
    queryFn: () =>
      authedJson<PurchasesData>(`${root}/widget/me/purchases?schoolID=${args.businessId}`, token),
  });
  // `schoolID` is NOT optional here, despite the controller reading it off
  // `req.query`. Omitting it sends `Review.find({ schoolId: undefined, ... })`,
  // and Mongoose strips an `undefined` condition rather than matching nothing —
  // so the customer got their reviews from EVERY school they have ever reviewed,
  // listed inside one school's `<baluu-profile>`. Scoped like `purchases` above,
  // and the school is in the query key for the same reason it is there: two
  // profiles for different schools must not share a cache entry.
  const reviews = useQuery({
    queryKey: ["me", "reviews", token, args.businessId],
    enabled,
    queryFn: () =>
      authedJson<ReviewsData>(`${root}/widget/me/reviews?schoolID=${args.businessId}`, token),
  });
  const waitlist = useQuery({
    queryKey: ["me", "waitlist", token],
    enabled,
    queryFn: () => authedJson<WaitlistData>(`${root}/widget/me/waitlist`, token),
  });
  const memberships = useQuery({
    queryKey: ["me", "memberships", token, args.businessId],
    enabled,
    queryFn: () =>
      authedJson<MembershipsData>(`${root}/widget/me/memberships?schoolId=${args.businessId}`, token),
  });
  const allQueries = [profile, purchases, reviews, waitlist, memberships];
  // First error across the 5 queries, surfaced for callers that just want to
  // know "did something fail" without inspecting each query individually.
  const error = allQueries.find((q) => q.error)?.error ?? null;
  const refetch = () => Promise.all(allQueries.map((q) => q.refetch()));

  // Drop back to anonymous on an expired session. This must be an effect,
  // not inline logic during render: `authStore.setAnonymous()`
  // unconditionally writes localStorage and notifies subscribers, and React
  // disallows side effects during render (fragile under Strict Mode's
  // double-invocation and concurrent rendering).
  //
  // Deliberately checks each query's error individually rather than the
  // aggregated `error` above: `error` is only the FIRST query (in array
  // order) that currently has an error, so if an earlier query fails with a
  // different error (e.g. HTTP 500) while a LATER query's token has
  // actually expired (401), keying off `error` alone would silently miss
  // that SESSION_EXPIRED. Depending on all 5 `.error` fields (rather than on
  // `error` or on the `allQueries` array, whose identity changes every
  // render) also keeps the effect from re-running on unrelated renders.
  useEffect(() => {
    const sessionExpired = [
      profile.error,
      purchases.error,
      reviews.error,
      waitlist.error,
      memberships.error,
    ].some((e) => (e as Error | null)?.message === "SESSION_EXPIRED");
    if (sessionExpired) authStore.setAnonymous();
  }, [profile.error, purchases.error, reviews.error, waitlist.error, memberships.error]);

  const leaveWaitlist = useMutation({
    // Route is keyed by school SLUG, not ID — the hosted widget calls
    // `/${school.slug}/waiting-list/${id}` (ProfileLoader.tsx:104), and the
    // API route is `DELETE /widget/:school/waiting-list/:id` where `:school`
    // is matched as a slug (apps/api/src/routes/widget/index.js:261).
    mutationFn: (waitlistId: string) =>
      fetch(`${root}/widget/${args.businessSlug}/waiting-list/${waitlistId}`, {
        method: "DELETE",
      }).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "waitlist"] }),
  });
  const openBillingPortal = useMutation({
    mutationFn: async (membershipSubscriptionId: string) => {
      const body = await authedJson<{ url: string }>(`${root}/widget/me/membership-portal`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ membershipSubscriptionId, returnUrl: window.location.href }),
      });
      window.location.assign(body.url);
    },
  });
  const updateProfile = useMutation({
    mutationFn: (patch: { firstName?: string; lastName?: string; phone?: string }) =>
      authedJson<ProfileResponse>(`${root}/widget/me/profile`, token, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "profile"] }),
  });

  return {
    profile,
    purchases,
    reviews,
    waitlist,
    memberships,
    leaveWaitlist,
    openBillingPortal,
    updateProfile,
    isLoading: allQueries.some((q) => q.isPending),
    error,
    refetch,
  };
}
